<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="520"
      persistent
      scrollable
    >
      <v-card
        v-if="selectedProduct"
        color="brand-card-bg"
      >
        <template v-if="!selectedProduct.Image">
          <v-toolbar
            color="brand-card-bg"
            flat
            height="80"
          >
            <v-toolbar-title>
              <div
                v-safe-html="getProductName(selectedProduct)"
                class="text-body brand-green-dark--text font-weight-bold"
              />

              <product-price
                :product="selectedProduct"
                auto-calculate
                class="brand-text-title brand-green-dark--text"
              />
            </v-toolbar-title>

            <v-spacer />

            <v-btn
              :height="$vuetify.breakpoint.smAndDown ? 36 : 50"
              :width="$vuetify.breakpoint.smAndDown ? 36 : 50"
              class="ma-1"
              color="brand-green-dark"
              depressed
              icon
              outlined
              style="background-color: #FFFFFF;"
              @click="onCancelButtonClick"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>

          <v-divider class="brand-green-dark mx-4" />
        </template>

        <v-card-text
          ref="cardText"
          class="pa-0 fill-height"
        >
          <v-btn
            v-if="selectedProduct.Image && !toolbarVisible"
            :height="$vuetify.breakpoint.smAndDown ? 36 : 50"
            :width="$vuetify.breakpoint.smAndDown ? 36 : 50"
            absolute
            class="safe-area-top dialog-cancel-button"
            color="brand-green-dark"
            depressed
            fab
            outlined
            right
            small
            style="top: 14px; background-color: #FFFFFF;"
            top
            @click="onCancelButtonClick"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>

          <div
            v-if="selectedProduct.Image"
            :style="`position: absolute; z-index: 3; height: ${toolbarHeight}px; width: 100%;`"
          >
            <v-scroll-y-transition>
              <div v-if="toolbarVisible">
                <v-toolbar
                  :height="toolbarHeight"
                  :style="`min-height: ${toolbarHeight}px; height: ${toolbarHeight}px; width: 100%;`"
                  class="py-2"
                  color="brand-card-bg"
                  flat
                  prominent
                >
                  <div
                    v-if="selectedProduct.Image && $vuetify.breakpoint.smAndUp"
                    style="width: 120px; height: 100px;"
                  >
                    <v-lazy min-height="100">
                      <img
                        :src="selectedProduct.Image"
                        style="width: 120px; height: 100px; border-radius: 0; object-fit: cover;"
                      >
                    </v-lazy>
                  </div>

                  <v-toolbar-title :class="$vuetify.breakpoint.smAndUp ? 'ml-4 mb-7' : 'ml-0 mb-2'">
                    <div class="text-body brand-green-dark--text">
                      <strong>{{ getProductName(selectedProduct) }}</strong>
                    </div>

                    <product-price
                      :product="selectedProduct"
                      auto-calculate
                      class="brand-text-title brand-green-dark--text"
                    />
                  </v-toolbar-title>

                  <v-spacer />

                  <v-btn
                    :height="$vuetify.breakpoint.smAndDown ? 36 : 50"
                    :width="$vuetify.breakpoint.smAndDown ? 36 : 50"
                    class="ma-1"
                    color="brand-green-dark"
                    depressed
                    icon
                    outlined
                    @click="onCancelButtonClick"
                  >
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>

                <v-divider class="brand-green-dark mx-4" />
              </div>
            </v-scroll-y-transition>
          </div>

          <v-img
            v-if="selectedProduct.Image"
            v-intersect.quiet="{
              handler: visibilityChanged,
              options: {
                threshold: 0.32
              }
            }"
            :src="selectedProduct.Image"
            max-height="350"
            min-height="120"
          >
            <v-container
              align-end
              fill-height
              fluid
            >
              <v-row>
                <v-col cols="12">
                  <div class="pa-2 brand-card-bg d-flex justify-space-between align-center">
                    <div class="text-body brand-green-dark--text d-flex">
                      {{ getProductName(selectedProduct) }}
                    </div>

                    <product-price
                      :product="selectedProduct"
                      auto-calculate
                      class="brand-text-title brand-green-dark--text d-flex pl-2"
                    />
                  </div>
                </v-col>
              </v-row>
            </v-container>
          </v-img>

          <div class="my-2 mx-4 brand-green-dark--text">
            <product-tags
              :tags="product.Tags"
              class="mt-3"
              small
            />

            <div v-if="!!selectedProduct.Description">
              <ellipsis-text
                :button-props="{text: false, depressed: true, tile: true, color: 'brand-primary'}"
                :lines="3"
                :text="getProductDescription(selectedProduct)"
                button-classes="px-1 brand-green-dark--text brand-text-small font-weight-bold"
                ellipsis
                text-classes="body-text mt-4 mx-1"
              />
            </div>

            <v-container
              class="mt-4"
              fluid
              pa-1
            >
              <template v-if="selectedProduct.Options && selectedProduct.Options.length">
                <template v-for="option in selectedProduct.Options">
                  <v-row
                    v-if="!option.Disabled"
                    :key="`product-option-${option.Id}-title`"
                  >
                    <v-col cols="12">
                      <template v-if="option.DisplayType === 'separator'">
                        <div
                          v-if="option.DisplayName"
                          class="separator-title"
                        >
                          <h3 class="d-inline-block">
                            {{ getTranslatedField(option) }}
                          </h3>
                        </div>
                        <v-divider v-else />
                      </template>

                      <h3 v-else>
                        {{ getTranslatedField(option) }}
                      </h3>

                      <v-alert
                        v-if="option.Error"
                        class="mb-0 mt-1 pa-1 caption"
                        color="red"
                        dense
                        text
                        type="warning"
                      >
                        {{ option.Error.Message }}
                      </v-alert>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="!option.Disabled"
                    :key="`product-option-${option.Id}`"
                    dense
                  >
                    <template
                      v-if="option.DisplayType === 'radio' || option.DisplayType === 'unit_value' || option.DisplayType === 'unit'"
                    >
                      <v-col cols="12">
                        <v-radio-group
                          v-model="option.DefaultValueId"
                          :disabled="option.Disabled"
                          :mandatory="option.Required"
                          class="ma-0 pa-0"
                          dense
                          hide-details
                        >
                          <template v-for="(value, idx) in option.OptionValues">
                            <v-radio
                              v-show="value.Visible !== false"
                              :key="`product-option-${option.Id}-${idx}-value-${value.Id}`"
                              :disabled="value.Disabled || option.Disabled"
                              :label="getTranslatedField(value)"
                              :value="value.Id"
                              class="ma-0 py-1"
                              color="brand-green-dark"
                              @click="onRadioClick(option, value)"
                            >
                              <template #label>
                                <v-label
                                  :class="[{'font-weight-bold': option.DefaultValueId === value.Id}]"
                                  class="text-capitalize v-label"
                                  style="left: 0px; right: auto; position: relative; cursor: pointer; height: auto;"
                                >
                                  {{ getTranslatedField(value) }}
                                </v-label>

                                <div
                                  v-if="value.ShowPrice && option.DisplayType !== 'unit'"
                                  :class="[{'font-weight-bold': option.DefaultValueId === value.Id}, parseFloat(value.Price) !== 0 ? (value.Mandatory && value.Selected || value.Disabled) ? 'grey--text text--lighten-1' : 'grey--text text--darken-1' : 'grey--text text--lighten-1']"
                                  class="brand-grey--text grow text-right"
                                >
                                  +{{ value.Price | currency }}
                                </div>
                              </template>
                            </v-radio>
                          </template>
                        </v-radio-group>
                      </v-col>
                    </template>

                    <template v-if="option.DisplayType === 'checkbox'">
                      <template v-for="(value, idx) in option.OptionValues">
                        <v-col
                          v-show="value.Visible !== false"
                          :key="`product-option-${option.Id}-${idx}-value-${value.Id}`"
                          cols="12"
                        >
                          <v-checkbox
                            v-model="value.Selected"
                            :disabled="(value.Mandatory && value.Selected) || value.Disabled"
                            :required="value.Mandatory"
                            :value="value.Selected"
                            class="mt-0 pt-0"
                            color="brand-green-dark"
                            dense
                            hide-details
                            @click="onCheckBoxClick(value)"
                          >
                            <template #label>
                              <v-label
                                v-safe-html="getTranslatedField(value)"
                                :class="{'font-weight-bold': value.Selected}"
                                :disabled="(value.Mandatory && value.Selected) || value.Disabled"
                                class="text-capitalize v-label"
                                style="left: 0px; right: auto; position: relative; cursor: pointer; height: auto;"
                              />

                              <div
                                v-if="value.ShowPrice"
                                :class="[{'font-weight-bold': value.Selected}, parseFloat(value.Price) !== 0 ? (value.Mandatory && value.Selected || value.Disabled) ? 'grey--text text--lighten-1' : 'grey--text text--darken-1' : 'grey--text text--lighten-1']"
                                class="brand-grey--text grow text-right"
                              >
                                +{{ value.Price | currency }}
                              </div>
                            </template>
                          </v-checkbox>
                        </v-col>
                      </template>
                    </template>
                  </v-row>
                </template>
              </template>

              <v-row>
                <v-col cols="12">
                  <v-textarea
                    v-model="selectedProduct.Comment"
                    :label="$t('Common.Button.Comments')"
                    auto-grow
                    color="brand-green-dark"
                    filled
                    rows="3"
                  />
                </v-col>
              </v-row>
            </v-container>
          </div>
        </v-card-text>

        <template v-if="storeCanSellItemsById(product.StoreId) || (!AppConfig.StoreClosed.DisableAddToCart && storeCanSellItemsById(product.StoreId))">
          <v-divider class="brand-green-dark mx-4" />

          <v-card-actions class="px-4 py-3">
            <product-dialog-quantity-selector
              v-model="productQuantity"
              :max="productMaxQuantity"
              :min="productMinQuantity"
              :step="productQuantityStep"
              :unit-id="selectedProduct.UnitId"
              quantity-animation
            />

            <v-spacer />

            <v-btn
              :x-large="$vuetify.breakpoint.mdAndUp"
              class="px-3"
              color="brand-primary brand-green-dark--text brand-text-title"
              depressed
              @click="onSaveButtonClick"
            >
              <span class="brand-text-title">{{ editMode ? $t('Cart.Button.UpdateOrder') : $t('Cart.Button.AddToOrder') }}</span>

              <v-spacer />

              <span
                class="ml-4 text-end brand-text-title"
                style="min-width: 75px;"
              >
                {{ productTotal | currency }}
              </span>
            </v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import ProductDetailsDialogMixin from '@/theme/default/components/product/ProductDetailsDialog.js'
import EllipsisText from '@/app-theme/components/common/EllipsisText.vue'

export default {
  name      : 'ProductDetailsDialog',
  components: {EllipsisText},
  mixins    : [ProductDetailsDialogMixin]
}
</script>

<style scoped src="@/theme/default/components/product/ProductDetailsDialog.css"></style>

<style scoped>
</style>
